import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import {
  CANCELLATION_REASON_LABELS,
  CANCELLATION_STATUS_LABELS,
  CancellationRequestDto,
  CancellationRequestReason,
  CancellationRequestStatus,
} from "../../../types/cancellation";
import CancellationRequestActions from "../../CancellationRequests/components/CancellationRequestActions";

type Props = {
  leadUlid: string;
  onAfterChange: () => void;
};

const STATUS_STYLES: Record<CancellationRequestStatus, { bg: string; fg: string }> = {
  DRAFT: { bg: "#e9ecef", fg: "#495057" },
  SIGNATURE_PENDING: { bg: "#fff3cd", fg: "#856404" },
  SIGNED: { bg: "#cfe2ff", fg: "#084298" },
  RECESSO_PENDING: { bg: "#fff3e0", fg: "#a15c00" },
  COMPLETED: { bg: "#d4edda", fg: "#155724" },
  REJECTED: { bg: "#fde2e2", fg: "#b32424" },
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function CancellationRequestFolderActions({
  leadUlid,
  onAfterChange,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState<CancellationRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<{ items: CancellationRequestDto[] }>(
        `/api/admin/operations/leads/${leadUlid}/cancellation-requests`,
        { token },
      );
      setItems(data.items);
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [leadUlid, token]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = () => {
    load();
    onAfterChange();
  };

  if (loading) {
    return (
      <div style={{ color: "#73879C", fontSize: ".85rem", padding: "0.5rem 0" }}>
        Cargando solicitudes…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          color: "#b32424",
          background: "#fde2e2",
          padding: "0.55rem 0.85rem",
          borderRadius: "0.5rem",
          fontSize: ".85rem",
          marginBottom: "0.5rem",
        }}
      >
        {error}
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {items.map((item) => (
        <RequestCard
          key={item.id}
          item={item}
          token={token}
          confirm={confirm}
          onReload={reload}
        />
      ))}
    </div>
  );
}

function RequestCard({
  item,
  token,
  confirm,
  onReload,
}: {
  item: CancellationRequestDto;
  token: string | null;
  confirm: ReturnType<typeof useConfirm>;
  onReload: () => void;
}) {
  // Per-lead endpoint only surfaces Tipo 1 rows, so casting to the narrower
  // status union is safe — Tipo 2/3 rows are filtered out server-side.
  const tipo1Status = item.status as CancellationRequestStatus;
  const statusStyle = STATUS_STYLES[tipo1Status] ?? {
    bg: "#e9ecef",
    fg: "#495057",
  };
  const reasonLabel =
    (item.reason &&
      CANCELLATION_REASON_LABELS[item.reason as CancellationRequestReason])
    || item.reasonLabel
    || item.reason
    || "";

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e9ecef",
        borderLeft: "3px solid #0069d9",
        borderRadius: "0.55rem",
        padding: "0.7rem 0.9rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.55rem",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.55rem",
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            display: "inline-block",
            padding: "0.15rem 0.55rem",
            borderRadius: "999px",
            background: statusStyle.bg,
            color: statusStyle.fg,
            fontSize: ".7rem",
            fontWeight: 700,
            letterSpacing: ".3px",
          }}
        >
          {CANCELLATION_STATUS_LABELS[tipo1Status] ?? item.status}
        </span>
        <strong style={{ color: "#2A3F54", fontSize: ".88rem" }}>
          {reasonLabel}
        </strong>
        <span
          style={{
            fontFamily: "monospace",
            fontSize: ".7rem",
            color: "#73879C",
          }}
          title={item.id}
        >
          {item.id.slice(-8)}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "0.35rem 0.9rem",
          fontSize: ".78rem",
        }}
      >
        <Field label="Solicitud" value={fmtDate(item.requestDate)} />
        <Field label="Comunicación" value={fmtDate(item.communicationDate)} />
        <Field
          label="Efectiva"
          value={fmtDate(item.effectiveCancellationDate)}
        />
        <Field label="Creada" value={fmtDateTime(item.createdAt)} />
        {item.signedAt && (
          <Field label="Firmada" value={fmtDateTime(item.signedAt)} />
        )}
        {item.completedAt && (
          <Field label="Completada" value={fmtDateTime(item.completedAt)} />
        )}
        {item.viafirmaId && (
          <Field
            label="Viafirma"
            value={`${item.viafirmaStatus ?? "—"} · ${item.viafirmaId.slice(-10)}`}
            mono
          />
        )}
      </div>

      <CancellationRequestActions
        row={item}
        token={token}
        confirm={confirm}
        onReload={onReload}
      />
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.1rem" }}>
      <span
        style={{
          fontSize: ".65rem",
          fontWeight: 700,
          color: "#73879C",
          textTransform: "uppercase",
          letterSpacing: ".3px",
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: "#1f2933",
          fontFamily: mono ? "monospace" : "inherit",
        }}
      >
        {value}
      </span>
    </div>
  );
}
